import React, { useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { MdMenu, MdClose } from "react-icons/md";
import theme from "theme";
import { headerBrand, navItems, urls } from "content/site";
import { Container, PrimaryButton } from "components/marketing/primitives";

const Bar = styled.header`
	position: sticky;
	top: 0;
	z-index: 50;
	background: ${theme.background.navBar};
	backdrop-filter: blur(12px);
	-webkit-backdrop-filter: blur(12px);
	border-bottom: 1px solid ${theme.color.border};
	box-shadow: ${theme.boxShadow.header};
`;

const BarAccent = styled.div`
	height: 2px;
	width: 100%;
	background: linear-gradient(90deg, ${theme.color.primary}, ${theme.color.secondary});
	opacity: 0.85;
`;

const BarInner = styled(Container)`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	min-height: 64px;
`;

const LogoLink = styled(Link)`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-shrink: 0;
	text-decoration: none;
	color: inherit;
	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 4px;
		border-radius: 4px;
	}
`;

const LogoMark = styled.img`
	width: ${headerBrand.iconWidthPx}px;
	height: ${headerBrand.iconHeightPx}px;
	object-fit: contain;
	display: block;
`;

const LogoWord = styled.span`
	font-family: ${theme.fontFamily.sans};
	font-size: 1.5rem;
	font-weight: 700;
	color: ${theme.color.dark};
	line-height: 1;
	letter-spacing: -0.02em;
`;

const Nav = styled.nav`
	display: none;
	align-items: center;
	gap: 0.35rem;
	margin-left: auto;
	@media (min-width: 900px) {
		display: flex;
	}
`;

const NavDashboardCta = styled(PrimaryButton)`
	margin-left: 0.35rem;
	padding: 0.5rem 1rem;
	font-size: 0.875rem;
`;

const NavRouterLink = styled(Link)`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.875rem;
	font-weight: 600;
	color: ${theme.color.darkL2};
	padding: 0.5rem 0.7rem;
	border-radius: ${theme.radius.sm};
	text-decoration: none;
	&:hover {
		color: ${theme.color.primary};
		background: ${theme.color.primaryMuted};
	}
	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 2px;
	}
`;

const NavHashLink = styled(HashLink)`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.875rem;
	font-weight: 600;
	color: ${theme.color.darkL2};
	padding: 0.5rem 0.7rem;
	border-radius: ${theme.radius.sm};
	text-decoration: none;
	&:hover {
		color: ${theme.color.primary};
		background: ${theme.color.primaryMuted};
	}
	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 2px;
	}
`;

const MenuToggle = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	border: 1px solid ${theme.color.border};
	background: ${theme.color.surface};
	border-radius: ${theme.radius.sm};
	cursor: pointer;
	color: ${theme.color.dark};
	@media (min-width: 900px) {
		display: none;
	}
	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 2px;
	}
`;

const MobilePanel = styled.div`
	display: ${(p) => (p.$open ? "flex" : "none")};
	flex-direction: column;
	gap: 0.5rem;
	padding: 1rem clamp(1rem, 4vw, 2rem) 1.25rem;
	background: ${theme.color.surface};
	border-bottom: 1px solid ${theme.color.border};
	@media (min-width: 900px) {
		display: none;
	}
`;

const MobileRouterLink = styled(Link)`
	font-family: ${theme.fontFamily.sans};
	font-weight: 600;
	padding: 0.65rem 0;
	color: ${theme.color.dark};
	text-decoration: none;
	border-bottom: 1px solid ${theme.color.border};
	&:last-of-type {
		border-bottom: none;
	}
	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 2px;
	}
`;

const MobileNavLink = styled(HashLink)`
	font-family: ${theme.fontFamily.sans};
	font-weight: 600;
	padding: 0.65rem 0;
	color: ${theme.color.dark};
	text-decoration: none;
	border-bottom: 1px solid ${theme.color.border};
	&:last-of-type {
		border-bottom: none;
	}
	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 2px;
	}
`;

export default function SiteHeader() {
	const [open, setOpen] = useState(false);

	const isHashNav = (to) => to.startsWith("/#") || to.startsWith("#");

	const renderDesktopNavItem = (item) => {
		if (isHashNav(item.to)) {
			return (
				<NavHashLink key={item.to} smooth to={item.to}>
					{item.label}
				</NavHashLink>
			);
		}
		return (
			<NavRouterLink key={item.to} to={item.to}>
				{item.label}
			</NavRouterLink>
		);
	};

	const renderMobileNavItem = (item) => {
		if (isHashNav(item.to)) {
			return (
				<MobileNavLink key={item.to} smooth to={item.to} onClick={() => setOpen(false)}>
					{item.label}
				</MobileNavLink>
			);
		}
		return (
			<MobileRouterLink key={item.to} to={item.to} onClick={() => setOpen(false)}>
				{item.label}
			</MobileRouterLink>
		);
	};

	return (
		<Bar>
			<BarAccent aria-hidden />
			<BarInner>
				<LogoLink to="/" aria-label="Truss Security home">
					<LogoMark src={headerBrand.iconSrc} alt="" width={headerBrand.iconWidthPx} height={headerBrand.iconHeightPx} />
					<LogoWord>{headerBrand.wordmark}</LogoWord>
				</LogoLink>
				<Nav aria-label="Primary">
					{navItems.map(renderDesktopNavItem)}
					<NavDashboardCta href={urls.dashboard}>Open dashboard</NavDashboardCta>
				</Nav>
				<MenuToggle
					type="button"
					aria-expanded={open}
					aria-controls="mobile-menu"
					aria-label={open ? "Close menu" : "Open menu"}
					onClick={() => setOpen((v) => !v)}
				>
					{open ? <MdClose size={24} /> : <MdMenu size={24} />}
				</MenuToggle>
			</BarInner>
			<MobilePanel id="mobile-menu" $open={open}>
				{navItems.map(renderMobileNavItem)}
				<PrimaryButton href={urls.dashboard} onClick={() => setOpen(false)}>
					Open dashboard
				</PrimaryButton>
			</MobilePanel>
		</Bar>
	);
}
